import { useContext, useState } from 'react';

import { getLinkAriaLabel, getUrlText } from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import {
  handleSubmenuArrowNavigation,
  navToFindStores,
} from '@/components/Megamenu/helpers';
import type {
  ContentfulMainMenuItemFragment,
  ContentfulMediaFragment,
} from '@/graphql/contentful/api';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { Scroller } from '../Scroller';

import * as S from './Submenu.styles';

export interface SubmenuProps {
  setActiveSubmenu: (isActive: string) => void;
  subMenu?: ContentfulMainMenuItemFragment;
}

export const Submenu = ({ setActiveSubmenu, subMenu }: SubmenuProps) => {
  const [image, setImage] = useState<ContentfulMediaFragment>(
    subMenu?.image as ContentfulMediaFragment,
  );
  const [menuUrl, setMenuUrl] = useState<string>(
    getUrlText(subMenu?.url ?? ''),
  );
  const locale = useContext(LocaleContext);
  const {
    country: { code },
    locale: { code: languageCode },
  } = useRegionProvider();
  const [mouseImageOver, setMouseImageOver] = useState<boolean>(false);

  let key = 0;
  let width = 50;
  const column = subMenu?.submenuItemsCollection?.items?.length as number;
  if (column > 3) width = 75;

  const onMouseHoverSubItem = (image: ContentfulMediaFragment, url: string) => {
    if (image) {
      setImage(image);
    }
    setMenuUrl(url);
  };

  const hasScrollerItems = Boolean(
    subMenu?.scrollerItemsCollection?.items?.length,
  );

  return (
    <S.SubmenuBox data-test-id='SubmenuBox'>
      <S.DesktopMegamenu>
        <S.MenuBox aria-label='Submenu' menuWidth={width} role='menu'>
          <ul data-test-id='SubmenuItemGroups'>
            {subMenu?.submenuItemsCollection?.items.map((itemGroup, index) => (
              <S.List
                key={'item' + key++}
                data-test-id={`SubmenuItem-${index}`}
                data-testid={`SubmenuItem-${index}`}
                role='none'
              >
                {(itemGroup?.submenuItemsCollection?.items.length as number) >
                  0 &&
                  itemGroup?.visibleInDesktop && (
                    <S.UL
                      key='itemGroup'
                      data-test-id='SubmenuItemGroup'
                      role='none'
                    >
                      {itemGroup?.url ? (
                        <LinkWithLocale
                          aria-label={getLinkAriaLabel(
                            getUrlText(itemGroup.url),
                            languageCode,
                          )}
                          as={
                            getUrlText(itemGroup.url) === '/stores'
                              ? 'a'
                              : undefined
                          }
                          className='itemGroupTitle'
                          href={
                            getUrlText(itemGroup.url) === '/stores'
                              ? navToFindStores(locale, code)
                              : getUrlText(itemGroup.url)
                          }
                          onClick={() => setActiveSubmenu('')}
                          onKeyDown={(e) => handleSubmenuArrowNavigation(e)}
                          onMouseOver={() =>
                            onMouseHoverSubItem(
                              itemGroup?.image as ContentfulMediaFragment,
                              itemGroup.url ?? '',
                            )
                          }
                          role='menuitem'
                          target={
                            itemGroup?.openUrlInNewTab ? '_blank' : '_self'
                          }
                        >
                          {itemGroup?.label}
                        </LinkWithLocale>
                      ) : (
                        <S.List
                          className='itemGroupTitle'
                          onMouseOver={() =>
                            onMouseHoverSubItem(
                              itemGroup?.image as ContentfulMediaFragment,
                              itemGroup?.url ?? '',
                            )
                          }
                        >
                          {itemGroup?.label}
                        </S.List>
                      )}

                      {itemGroup?.submenuItemsCollection?.items.map(
                        (item) =>
                          item?.visibleInDesktop &&
                          item?.url && (
                            <S.List
                              key={item?.internalName}
                              className='subMenuItem'
                              data-test-id={item?.internalName}
                              role='none'
                            >
                              <S.Item
                                aria-label={getLinkAriaLabel(
                                  getUrlText(item.url),
                                  languageCode,
                                )}
                                as={
                                  getUrlText(item.url) === '/stores'
                                    ? 'a'
                                    : undefined
                                }
                                data-testid={`subMenuItemLink-${item?.internalName}`}
                                href={
                                  getUrlText(item.url) === '/stores'
                                    ? navToFindStores(locale, code)
                                    : getUrlText(item.url)
                                }
                                onClick={() => setActiveSubmenu('')}
                                onKeyDown={(e) =>
                                  handleSubmenuArrowNavigation(e)
                                }
                                onMouseOver={() =>
                                  onMouseHoverSubItem(
                                    item?.image as ContentfulMediaFragment,
                                    item.url ?? '',
                                  )
                                }
                                role='menuitem'
                                target={
                                  item?.openUrlInNewTab ? '_blank' : '_self'
                                }
                              >
                                {item?.label}
                              </S.Item>
                            </S.List>
                          ),
                      )}
                    </S.UL>
                  )}
              </S.List>
            ))}
          </ul>
        </S.MenuBox>
        {hasScrollerItems ? (
          <Scroller
            menuItem={subMenu as ContentfulMainMenuItemFragment}
            onNavigate={() => setActiveSubmenu('')}
            variant='desktop'
          />
        ) : (
          <S.IMGBox
            key={image?.url ?? ''}
            $menuWidth={100 - width}
            aria-label={getLinkAriaLabel(menuUrl, languageCode)}
            as={menuUrl ? LinkWithLocale : 'div'}
            data-test-id='SubmenuIMGBox'
            href={menuUrl ?? ''}
            onMouseLeave={() => setMouseImageOver(false)}
            onMouseOver={() => setMouseImageOver(true)}
          >
            <AspectRatioImage
              alt={image?.title ?? ''}
              aspectRatio={{
                x: width === 50 ? 17 : 3,
                y: width === 50 ? 10 : 4,
              }}
              className='image submenubox'
              focalPoint={{
                x: (image?.width ?? 0) / 2,
                y: (image?.height ?? 0) / 2,
              }}
              loaderProps={{ height: 175 }}
              loading='lazy'
              originalImageDimensions={{
                x: image?.width ?? 0,
                y: image?.height ?? 0,
              }}
              scale={mouseImageOver ? 1.1 : 1}
              showFallbackPlaceholder
              sizes={`(max-width: 600px) 100vw, ${100 - width}vw`}
              src={image?.url ?? ''}
              useLoader
              width='100%'
            />
          </S.IMGBox>
        )}
      </S.DesktopMegamenu>
    </S.SubmenuBox>
  );
};

Submenu.displayName = 'Submenu';
